import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ReservationTimer from '../components/ReservationTimer';
import ProgressBar from '../components/ProgressBar';
import ProviderImage from '../components/ProviderImage';
import { useAppState } from '../state/AppState';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { createPatient, bookAppointment, uploadImage } from '../services/sessionService';
import { getConfig } from '../services/configService';
import ErrorModal, {
  GENERIC_ERROR_MODAL_MESSAGE,
  GENERIC_ERROR_MODAL_TITLE,
} from '../components/ErrorModal';
import DuplicatePatientErrorModal from '../components/DuplicatePatientErrorModal';
import { isIOS, isMobile } from '../utils/deviceUtils';
import { formatSessionDate } from '../utils/dateUtils';
import { hasInsurance, isSelfPay } from '../utils/insuranceUtils';
import { processImageFile, fileToBase64 } from '../services/imageService';

/** "How did you hear about us" options (order preserved; values sent as marketingSource) */
const MARKETING_SOURCE_OPTIONS = [
  'Billboard',
  'Bing',
  'Google',
  'Insurance',
  'Podcast',
  'Referral by Mindful Provider',
  'Referral by Non-Mindful Provider',
  'Psychology Today',
  'Radio',
  'School/Teacher',
  'Social Media',
  'Television',
  'Friend/Family',
  'Yelp',
  'Other'
] as const;

const MARKETING_SOURCE_OTHER = 'Other';

/**
 * ScreenP - Comprehensive patient intake form screen.
 *
 * Allows the user to complete a detailed intake form with personal information,
 * primary subscriber details, insurance information, and communication preferences.
 */
const ScreenP: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  
  // Personal Information
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateValue, setStateValue] = useState('WA');
  const [zipCode, setZipCode] = useState('');

  // Primary Subscriber Information
  const [isPrimarySubscriber, setIsPrimarySubscriber] = useState('Yes');
  const [connectionToSubscriber, setConnectionToSubscriber] = useState('Spouse');
  const [subscriberFirst, setSubscriberFirst] = useState('');
  const [subscriberLast, setSubscriberLast] = useState('');
  const [subscriberDob, setSubscriberDob] = useState('');
  const [subscriberDifferentAddress, setSubscriberDifferentAddress] = useState('No');
  const [subscriberAddress, setSubscriberAddress] = useState('55 Street Ave.');
  const [subscriberCity, setSubscriberCity] = useState('City');
  const [subscriberState, setSubscriberState] = useState('WA');
  const [subscriberZipCode, setSubscriberZipCode] = useState('55555');

  // Insurance Information
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insuranceId, setInsuranceId] = useState('');
  
  // Secondary and Tertiary Insurance
  const [secondaryInsuranceProvider, setSecondaryInsuranceProvider] = useState('');
  const [secondaryInsuranceId, setSecondaryInsuranceId] = useState('');
  const [tertiaryInsuranceProvider, setTertiaryInsuranceProvider] = useState('');
  const [tertiaryInsuranceId, setTertiaryInsuranceId] = useState('');

  // Communication Preferences
  const [heardAboutUs, setHeardAboutUs] = useState('');
  const [smsReminders, setSmsReminders] = useState(false);

  // Form Error State
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});
  
  // Error Modal State
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Duplicate Patient Error Modal State
  const [duplicatePatientModal, setDuplicatePatientModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // Insurance Modal State
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  
  // Saving state
  const [isSaving, setIsSaving] = useState(false);
  
  // Uploaded Images State
  const [uploadedImages, setUploadedImages] = useState<{ [key: string]: string }>({});

  // Clear field errors when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Preserve scroll position when conditional content appears/disappears
  const scrollPositionRef = useRef<number | null>(null);
  
  // Handler to preserve scroll when isPrimarySubscriber changes
  const handlePrimarySubscriberChange = (value: string) => {
    scrollPositionRef.current = window.scrollY;
    setIsPrimarySubscriber(value);
  };
  
  // Handler to preserve scroll when subscriberDifferentAddress changes
  const handleSubscriberDifferentAddressChange = (value: string) => {
    scrollPositionRef.current = window.scrollY;
    setSubscriberDifferentAddress(value);
  };
  
  useEffect(() => {
    if (scrollPositionRef.current !== null) {
      // Restore scroll position after DOM updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollPositionRef.current !== null) {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = null;
          }
        });
      });
    }
  }, [isPrimarySubscriber, subscriberDifferentAddress]);

  // Check for insurance on component mount - removed automatic modal
  // useEffect(() => {
  //   if (state.insurance && state.insurance.trim() !== '') {
  //     setShowInsuranceModal(true);
  //   }
  // }, [state.insurance]);

  // Initialize isPrimarySubscriber from visitType
  useEffect(() => {
    if (state.visitType) {
      if (state.visitType === 'Myself') {
        setIsPrimarySubscriber('Yes');
      } else {
        // Default to "No" if visitType is not "Myself"
        setIsPrimarySubscriber('No');
      }
    }
  }, [state.visitType]);

  // Load phone from state on component mount and when state.phone changes
  useEffect(() => {
    if (state.phone) {
      // Format the phone number if it's not already formatted
      const formattedPhone = formatPhoneNumber(state.phone);
      // Always update if state.phone exists (even if phone field already has a value)
      // This ensures we populate from continue response even if user navigated away and back
      setPhone(formattedPhone);
    } else if (!state.phone && phone) {
      // If state.phone is cleared but phone field has value, keep the phone field value
      // (user may have typed it manually)
    }
  }, [state.phone]); // Only depend on state.phone, not phone itself

  // Initialize smsReminders from state.textConsent
  useEffect(() => {
    if (state.textConsent !== null && state.textConsent !== undefined) {
      setSmsReminders(state.textConsent);
    }
  }, [state.textConsent]);

  // Pre-select primary insurance dropdown when single insurance is selected
  useEffect(() => {
    if (hasInsurance(state.insurance) && state.insurance) {
      const insurances = state.insurance.split(', ');
      if (insurances.length === 1 && !insuranceProvider) {
        setInsuranceProvider(insurances[0].toLowerCase().replace(/\s+/g, '-'));
      }
    }
  }, [state.insurance]);

  // Debug dob state changes
  useEffect(() => {
  }, [dob]);

  // Load previously uploaded images from session data on component mount
  useEffect(() => {
    if (state.sessionData && state.sessionData.images && Array.isArray(state.sessionData.images)) {
      const newUploadedImages: { [key: string]: string } = {};
      
      state.sessionData.images.forEach((image: any) => {
        if (image.filename && image.base64Content) {
          // Extract the insurance type and card side from filename
          // New format: "{Primary/Secondary/Tertiary}_{InsuranceName}_{Front/Back}.{ext}"
          // Old format (for backward compatibility): "{insuranceType}-insurance-{cardSide}.jpg"
          
          let imageKey: string | null = null;
          
          // Try new format first (underscore-separated)
          if (image.filename.includes('_')) {
            const parts = image.filename.split('_');
            if (parts.length >= 3) {
              const label = parts[0]; // "Primary", "Secondary", "Tertiary"
              const insuranceName = parts.slice(1, -1).join('_'); // Everything except first and last
              const cardSideWithExt = parts[parts.length - 1]; // Last part: "Front.jpg" or "Back.jpg"
              const cardSide = cardSideWithExt.split('.')[0].toLowerCase(); // "front" or "back"
              
              // Find the insurance in state.insurance and get its ID
              if (state.insurance) {
                const insuranceArray = state.insurance.split(', ');
                const insuranceIndex = insuranceArray.findIndex(ins => ins === insuranceName);
                if (insuranceIndex >= 0) {
                  const insuranceId = insuranceArray[insuranceIndex].toLowerCase().replace(/\s+/g, '-');
                  imageKey = `${insuranceId}-${cardSide}`;
                }
              }
            }
          } 
          // Fallback to old format for backward compatibility
          else if (image.filename.includes('-')) {
            const filenameParts = image.filename.split('-');
            if (filenameParts.length >= 3) {
              const insuranceType = filenameParts[0]; // "primary", "secondary", etc.
              const cardSide = filenameParts[2].split('.')[0]; // "front", "back"
              imageKey = `${insuranceType}-${cardSide}`;
            }
          }
          
          if (imageKey) {
            // Determine MIME type from file extension
            const extension = image.filename.split('.').pop()?.toLowerCase() || 'jpg';
            const mimeType = extension === 'png' ? 'image/png' : 
                            extension === 'gif' ? 'image/gif' : 
                            'image/jpeg';
            
            // Create a data URL from the base64 content
            const dataUrl = `data:${mimeType};base64,${image.base64Content}`;
            newUploadedImages[imageKey] = dataUrl;
          }
        }
      });
      
      if (Object.keys(newUploadedImages).length > 0) {
        setUploadedImages(newUploadedImages);
      }
    }
  }, [state.sessionData, state.insurance]);

  // Cleanup object URLs on component unmount
  useEffect(() => {
    return () => {
      Object.values(uploadedImages).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
        // Note: data URLs don't need cleanup as they're not object URLs
      });
    };
  }, [uploadedImages]);

  // Helper function to format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as XXX-XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return phoneNumber;
  };

  // Helper function to validate phone number format
  const isValidPhoneNumber = (phone: string) => {
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  // Helper function to format zip code (5 digits or 5+4 format)
  const formatZipCode = (value: string) => {
    // Remove all non-numeric characters
    const zip = value.replace(/\D/g, '');
    
    // Format as XXXXX or XXXXX-XXXX
    if (zip.length > 5) {
      return `${zip.slice(0, 5)}-${zip.slice(5, 9)}`;
    }
    return zip;
  };

  // Helper function to get available insurances for each select box
  const getAvailableInsurances = (currentSelection: string, otherSelections: string[]) => {
    if (!state.insurance) return [];
    
    const allInsurances = state.insurance.split(', ');
    const selectedInsurances = otherSelections.filter(sel => sel !== '');
    
    // Convert primary insurance provider value back to original name for comparison
    const primaryInsuranceName = insuranceProvider ? 
      allInsurances.find(ins => ins.toLowerCase().replace(/\s+/g, '-') === insuranceProvider) || '' : '';
    
    // Convert normalized values in otherSelections back to original names
    const otherSelectedNames = selectedInsurances.map(normalizedValue => {
      return allInsurances.find(ins => ins.toLowerCase().replace(/\s+/g, '-') === normalizedValue) || '';
    }).filter(name => name !== '');
    
    // Get all selected insurance names (including primary)
    const allSelectedNames = [
      primaryInsuranceName,
      ...otherSelectedNames
    ].filter(name => name !== '');
    
    // Return all insurances except those selected in other boxes
    return allInsurances.filter(insurance => 
      !allSelectedNames.includes(insurance)
    );
  };

  // Helper function to validate zip code format
  const isValidZipCode = (zip: string) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  // Helper function to validate email format
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


  // Helper function to show error modal (always generic user-facing copy)
  const showError = (_title: string, _message: string) => {
    setErrorModal({
      isOpen: true,
      title: GENERIC_ERROR_MODAL_TITLE,
      message: GENERIC_ERROR_MODAL_MESSAGE
    });
  };

  const pushAmdErrorToDataLayer = (errorMessage: string) => {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'IOI Error',
        'IOI Error Message': errorMessage
      });
    }
  };

  // Helper function to show duplicate patient error modal
  const showDuplicatePatientError = () => {
    setDuplicatePatientModal({
      isOpen: true
    });
  };

  // Helper function to close duplicate patient error modal
  const closeDuplicatePatientError = () => {
    setDuplicatePatientModal({
      isOpen: false
    });
  };

  // Helper function to redirect to portal
  const redirectToPortal = () => {
    const portalUrl = `https://pp-wfe-100.advancedmd.com/${state.licenseKey}/account/logon`;
    window.open(portalUrl, '_blank');
    closeDuplicatePatientError();
  };

  // Helper function to close error modal
  const closeErrorModal = () => {
    setErrorModal({
      isOpen: false,
      title: '',
      message: ''
    });
  };



  // Helper function to check if all required insurance card images are uploaded
  const areAllRequiredImagesUploaded = () => {
    if (isSelfPay(state.insurance)) {
      return true; // No images required for self-pay
    }
    
    if (!state.insurance) {
      return true; // No insurance selected
    }
    
    const insuranceList = state.insurance.split(', ');
    
    for (const insurance of insuranceList) {
      const insuranceId = insurance.toLowerCase().replace(/\s+/g, '-');
      const frontImageKey = `${insuranceId}-front`;
      const backImageKey = `${insuranceId}-back`;
      
      if (!uploadedImages[frontImageKey] || !uploadedImages[backImageKey]) {
        return false;
      }
    }
    
    return true;
  };

  // Helper function to handle file upload
  const handleFileUpload = async (file: File, insuranceId: string, cardSide: 'front' | 'back') => {
    try {
      // Process image file (convert HEIC to JPEG if needed)
      let fileToProcess: File;
      let previewFile: File;
      
      try {
        const processed = await processImageFile(file);
        fileToProcess = processed.file;
        previewFile = processed.previewFile;
      } catch (conversionError) {
        showError('Conversion Error', 'Failed to convert HEIC file. Please try a different image format.');
        return;
      }
      
      // Determine file extension IMMEDIATELY after processing based on the actual file type
      // This ensures converted HEIC files are correctly identified as JPEG
      let fileExtension = 'jpg'; // default
      if (fileToProcess.type) {
        const mimeType = fileToProcess.type.toLowerCase();
        if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
          fileExtension = 'jpg';
        } else if (mimeType.includes('png')) {
          fileExtension = 'png';
        } else if (mimeType.includes('gif')) {
          fileExtension = 'gif';
        } else if (mimeType.includes('webp')) {
          fileExtension = 'webp';
        }
      }
      
      // Determine insurance label (Primary/Secondary/Tertiary) and name
      const insuranceLabels = ['Primary', 'Secondary', 'Tertiary'];
      let insuranceLabel = 'Primary';
      let insuranceName = '';
      
      if (state.insurance) {
        const insuranceArray = state.insurance.split(', ');
        const insuranceIndex = insuranceArray.findIndex(ins => 
          ins.toLowerCase().replace(/\s+/g, '-') === insuranceId
        );
        
        if (insuranceIndex >= 0) {
          insuranceLabel = insuranceLabels[insuranceIndex] || `Insurance ${insuranceIndex + 1}`;
          insuranceName = insuranceArray[insuranceIndex];
        } else {
          // Fallback: use insuranceId if not found
          insuranceName = insuranceId;
        }
      } else {
        // Fallback: use insuranceId if state.insurance is not available
        insuranceName = insuranceId;
      }
      
      // Generate filename: {Primary/Secondary/Tertiary}_{InsuranceName}_{Front/Back}.{ext}
      // The extension is determined from the processed file's MIME type above
      const cardSideCapitalized = cardSide.charAt(0).toUpperCase() + cardSide.slice(1);
      const filename = `${insuranceLabel}_${insuranceName}_${cardSideCapitalized}.${fileExtension}`;
      const description = `${insuranceLabel} ${insuranceName} insurance card (${cardSide})`;
      
      // Convert file to base64
      const base64Content = await fileToBase64(fileToProcess);
      
      // Get API URL
      const config = getConfig();
      const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';
      
      // Upload the image
      const uploadSuccess = await uploadImage(filename, description, base64Content, baseUrl);
      
      if (uploadSuccess) {
        // Update state to track uploaded image
        const imageKey = `${insuranceId}-${cardSide}`;
        
        dispatch({ 
          type: 'SET_UPLOADED_IMAGE', 
          payload: { 
            key: imageKey, 
            filename: filename 
          } 
        });
        
        // Clear any field errors for this image
        if (fieldErrors[imageKey]) {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[imageKey];
            return newErrors;
          });
        }
        
        // Store image preview for display (use converted JPEG if it was HEIC)
        setUploadedImages(prev => ({
          ...prev,
          [imageKey]: URL.createObjectURL(previewFile)
        }));
      } else {
        showError('Upload Failed', `Failed to upload ${insuranceLabel} ${insuranceName} insurance card (${cardSide}). Please try again.`);
      }
    } catch (error) {
      showError('Upload Error', 'An error occurred while uploading the file. Please try again.');
    }
  };

  // Helper function to format time as "12pm - 1pm" (one hour block)
  const formatSessionTime = (timeString: string | null) => {
    try {
      if (!timeString) return '12pm - 1pm';
      
      // Parse the time string (assuming format like "12:00 PM" or "12pm")
      const time = timeString.toLowerCase().replace(/\s+/g, '');
      const timeMatch = time.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/);
      
      if (!timeMatch) return '12pm - 1pm';
      
      const hour = parseInt(timeMatch[1]);
      const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
      const period = timeMatch[3];
      
      // Create start time
      const startHour = period === 'pm' && hour !== 12 ? hour + 12 : (period === 'am' && hour === 12 ? 0 : hour);
      const startTime = new Date();
      startTime.setHours(startHour, minute, 0, 0);
      
      // Create end time (one hour later)
      const endTime = new Date(startTime);
      endTime.setHours(endTime.getHours() + 1);
      
      // Format both times
      const formatTime = (date: Date) => {
        const hour = date.getHours();
        const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
        const period = hour < 12 ? 'am' : 'pm';
        return `${displayHour}${period}`;
      };
      
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    } catch (error) {
      return '12pm - 1pm';
    }
  };

  // Format appointment data using the same functions as ScreenT
  const sessionDate = formatSessionDate(state.appointmentDate);
  const sessionTime = formatSessionTime(state.appointmentTime);
  const sessionLocation = state.location 
    ? (state.locationAddress ? `${state.location}\n${state.locationAddress}` : state.location)
    : '1234 Mockup Street\nSuite 202\nSeattle, WA 98122';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing errors
    setFormError(null);
    setFieldErrors({});
    
    const newFieldErrors: { [key: string]: boolean } = {};
    
    // Basic validation
    if (!first) newFieldErrors.first = true;
    if (!last) newFieldErrors.last = true;
    if (!dob) newFieldErrors.dob = true;
    if (!sex) newFieldErrors.sex = true;
    if (!phone) newFieldErrors.phone = true;
    if (!email) newFieldErrors.email = true;
    if (!address) newFieldErrors.address = true;
    if (!city) newFieldErrors.city = true;
    if (!zipCode) newFieldErrors.zipCode = true;
    
    // Phone number validation
    if (phone && !isValidPhoneNumber(phone)) {
      newFieldErrors.phone = true;
    }
    
    // Zip code validation
    if (zipCode && !isValidZipCode(zipCode)) {
      newFieldErrors.zipCode = true;
    }
    
    // Email validation
    if (email && !isValidEmail(email)) {
      newFieldErrors.email = true;
    }
    
    // If not primary subscriber, validate subscriber fields
    if (isPrimarySubscriber === 'No') {
      if (!subscriberFirst) newFieldErrors.subscriberFirst = true;
      if (!subscriberLast) newFieldErrors.subscriberLast = true;
      if (!subscriberDob) newFieldErrors.subscriberDob = true;
      
      // Validate subscriber zip code if different address is selected
      if (subscriberDifferentAddress === 'Yes' && subscriberZipCode && !isValidZipCode(subscriberZipCode)) {
        newFieldErrors.subscriberZipCode = true;
      }
    }
    
    // Validate insurance fields if not self-pay
    if (hasInsurance(state.insurance) && state.insurance) {
      const insuranceCount = state.insurance.split(', ').length;
      
      // Primary insurance is always required
      if (!insuranceProvider) newFieldErrors.insuranceProvider = true;
      if (!insuranceId) newFieldErrors.insuranceId = true;
      
      // Secondary insurance is required if 2+ insurances are selected
      if (insuranceCount >= 2) {
        if (!secondaryInsuranceProvider) {
          const secondaryInsuranceIdKey = state.insurance.split(', ')[1]?.toLowerCase().replace(/\s+/g, '-');
          if (secondaryInsuranceIdKey) {
            newFieldErrors[`${secondaryInsuranceIdKey}-provider`] = true;
          }
        }
        if (!secondaryInsuranceId) {
          const secondaryInsuranceIdKey = state.insurance.split(', ')[1]?.toLowerCase().replace(/\s+/g, '-');
          if (secondaryInsuranceIdKey) {
            newFieldErrors[`${secondaryInsuranceIdKey}-id`] = true;
          }
        }
      }
      
      // Tertiary insurance is required if 3 insurances are selected
      if (insuranceCount >= 3) {
        if (!tertiaryInsuranceProvider) {
          const tertiaryInsuranceIdKey = state.insurance.split(', ')[2]?.toLowerCase().replace(/\s+/g, '-');
          if (tertiaryInsuranceIdKey) {
            newFieldErrors[`${tertiaryInsuranceIdKey}-provider`] = true;
          }
        }
        if (!tertiaryInsuranceId) {
          const tertiaryInsuranceIdKey = state.insurance.split(', ')[2]?.toLowerCase().replace(/\s+/g, '-');
          if (tertiaryInsuranceIdKey) {
            newFieldErrors[`${tertiaryInsuranceIdKey}-id`] = true;
          }
        }
      }
    }
    
    // Set field errors
    setFieldErrors(newFieldErrors);
    
    // If there are field errors, show a general message and scroll to first error
    if (Object.keys(newFieldErrors).length > 0) {
      setFormError('Please fill in all required fields marked in red.');
      
      // Scroll to the first field with an error
      const firstErrorField = Object.keys(newFieldErrors)[0];
      // Map error field names to actual DOM IDs
      const fieldIdMap: { [key: string]: string } = {
        'first': 'first-name',
        'last': 'last-name',
        'dob': 'date-of-birth',
        'sex': 'sex',
        'phone': 'phone',
        'email': 'email',
        'address': 'address',
        'city': 'city',
        'state': 'state',
        'zipCode': 'zip-code',
        'subscriberFirst': 'subscriber-first',
        'subscriberLast': 'subscriber-last',
        'subscriberDob': 'subscriber-dob',
        'subscriberZipCode': 'subscriber-zip-code',
        'insuranceProvider': 'insurance-provider',
        'insuranceId': 'insurance-id'
      };
      
      const fieldId = fieldIdMap[firstErrorField] || firstErrorField;
      const errorElement = document.getElementById(fieldId);
      if (errorElement) {
        // Small delay to ensure DOM is updated with error styling
        setTimeout(() => {
          errorElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Focus the element for better accessibility
          errorElement.focus();
        }, 100);
      } else {
        const formElement = document.getElementById('form-title');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return;
    }
    
    // Validate insurance card images if insurance is selected (not self-pay)
    if (hasInsurance(state.insurance) && state.insurance) {
      const insuranceList = state.insurance.split(', ');
      const missingImages: string[] = [];
      
      insuranceList.forEach((insurance, index) => {
        const insuranceLabels = ['Primary', 'Secondary', 'Tertiary'];
        const label = insuranceLabels[index] || `Insurance ${index + 1}`;
        const insuranceId = insurance.toLowerCase().replace(/\s+/g, '-');
        
        // Check if both front and back images are uploaded for this insurance
        const frontImageKey = `${insuranceId}-front`;
        const backImageKey = `${insuranceId}-back`;
        
        if (!uploadedImages[frontImageKey]) {
          newFieldErrors[frontImageKey] = true;
          missingImages.push(`${label} insurance card (front)`);
        }
        if (!uploadedImages[backImageKey]) {
          newFieldErrors[backImageKey] = true;
          missingImages.push(`${label} insurance card (back)`);
        }
      });
      
      // Update field errors with image errors
      setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
      
      if (missingImages.length > 0) {
        setFormError('Please fill in all required fields marked in red.');
        
        // Scroll to the first missing image upload area
        const firstMissingImage = Object.keys(newFieldErrors).find(key => key.includes('-front') || key.includes('-back'));
        if (firstMissingImage) {
          // Find the upload container by looking for the insurance card upload section
          const uploadSection = document.querySelector(`[data-insurance-id="${firstMissingImage.split('-')[0]}"]`);
          if (uploadSection) {
            uploadSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }
        return;
      }
    }
    
    // Create patient via API
    setIsSaving(true);
    try {
      // Get the correct API URL from config or state
      const config = getConfig();
      const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';
      // Format date of birth as MM/DD/YYYY
      const formatDob = (dateString: string) => {
        // Check if dateString is empty or invalid
        if (!dateString || dateString === '') {
          return '01/01/1995'; // This should reveal if we're hitting this case
        }
        
        // Parse the date string manually to avoid timezone issues
        // dateString is in YYYY-MM-DD format from HTML date input
        const [year, month, day] = dateString.split('-');
        const result = `${month}/${day}/${year}`;
        return result;
      };
      
      // Determine relationship based on primary subscriber status
      // For API: "SELF" or "1" when isPrimarySubscriber is "Yes"
      // For API: "SPOUSE", "CHILD", "OTHER" when isPrimarySubscriber is "No"
      let relationship: string;
      if (isPrimarySubscriber === 'Yes') {
        relationship = 'SELF'; // or "1" - using "SELF" as per API spec
      } else {
        // Map connectionToSubscriber to API relationship values
        const relationshipMap: { [key: string]: string } = {
          'Spouse': 'SPOUSE',
          'Child': 'CHILD',
          'Other': 'OTHER'
        };
        relationship = relationshipMap[connectionToSubscriber] || 'OTHER';
      }
      
      // Store relationship in state for session and potential use in bookAppointment
      // Use lowercase for state storage (for backward compatibility with bookAppointment)
      dispatch({ type: 'SET_RELATIONSHIP', payload: relationship.toLowerCase() });
      
      // Format phone number for API (remove dashes)
      const formatPhoneForApi = (phone: string) => {
        return phone.replace(/-/g, '');
      };
      
      // Create patient data object
      const patientData: any = {
        name: `${last}, ${first}`,
        profile: state.profileId?.toString() || "3", // Use profileId from state, default to "3"
        sendApptReminderText: smsReminders,
        sex: sex,
        relationship: relationship,
        dob: formatDob(dob),
        ssn: "", // Not collected in form
        chart: "AUTO",
        selfReportedSource:
          heardAboutUs === MARKETING_SOURCE_OTHER ? (state.selfReportedSource || null) : null,
        marketingSource: heardAboutUs || null,
        address: {
          zip: zipCode,
          city: city,
          state: stateValue,
          address2: address
        },
        contactInfo: {
          homePhone: `(${formatPhoneForApi(phone).slice(0, 3)})${formatPhoneForApi(phone).slice(3, 6)}-${formatPhoneForApi(phone).slice(6)}`,
          otherPhone: "", // Not collected in form
          otherType: "C", // Default value
          email: email,
          preferredCommunicationFid: "3" // Default value
        }
      };
      
      // Add responsibleParty only when isPrimarySubscriber is "No"
      if (isPrimarySubscriber === 'No') {
        // Determine address for responsibleParty
        // If subscriberDifferentAddress is "No", use patient's address
        // If subscriberDifferentAddress is "Yes", use subscriber's address
        const responsiblePartyAddress = subscriberDifferentAddress === 'Yes' 
          ? {
              address1: subscriberAddress,
              city: subscriberCity,
              state: subscriberState,
              zip: subscriberZipCode
            }
          : {
              address1: address,
              city: city,
              state: stateValue,
              zip: zipCode
            };
        
        patientData.responsibleParty = {
          name: `${subscriberLast}, ${subscriberFirst}`,
          dob: formatDob(subscriberDob),
          address: responsiblePartyAddress,
          contactInfo: {
            // Optional fields - can be omitted or set to empty strings
            otherPhone: "",
            otherType: "C",
            email: ""
          }
        };
      }
      // Note: When isPrimarySubscriber is "Yes", we do NOT include responsibleParty
      // The API will automatically set @respparty = "SELF"
      
      // Build insurances array (only include if not self-pay and both provider and ID are provided)
      const insurances: Array<{ carrierName: string; subscriberNum: string }> = [];
      
      if (hasInsurance(state.insurance) && state.insurance) {
        const allInsurances = state.insurance.split(', ');
        // Helper function to convert insurance provider value back to original name
        const getInsuranceName = (providerValue: string): string | null => {
          if (!providerValue) {
            return null;
          }
          const found = allInsurances.find(ins => ins.toLowerCase().replace(/\s+/g, '-') === providerValue);
          return found || null;
        };
        
        // Primary insurance
        if (insuranceProvider && insuranceId) {
          const carrierName = getInsuranceName(insuranceProvider);
          if (carrierName) {
            insurances.push({
              carrierName: carrierName.toUpperCase(),
              subscriberNum: insuranceId
            });
          } else {
          }
        }
        
        // Secondary insurance
        if (secondaryInsuranceProvider && secondaryInsuranceId) {
          const carrierName = getInsuranceName(secondaryInsuranceProvider);
          if (carrierName) {
            insurances.push({
              carrierName: carrierName.toUpperCase(),
              subscriberNum: secondaryInsuranceId
            });
          } else {
          }
        } else {
        }
        
        // Tertiary insurance
        if (tertiaryInsuranceProvider && tertiaryInsuranceId) {
          const carrierName = getInsuranceName(tertiaryInsuranceProvider);
          if (carrierName) {
            insurances.push({
              carrierName: carrierName.toUpperCase(),
              subscriberNum: tertiaryInsuranceId
            });
          } else {
          }
        } else {
        }
      }
      
      // Add insurances array to patientData (can be empty array)
      patientData.insurances = insurances;
      const createPatientResult = await createPatient(patientData, baseUrl);
      
      if (createPatientResult.success && createPatientResult.patientId) {
        // Book the appointment
        try {
          // Get provider code from state
          const providerCode = state.providerId;
          if (!providerCode) {
            showError('Booking Error', 'No provider information available. Please try again.');
            return;
          }
          
          // Get location state from state
          const locationState = state.stateString || 'Colorado'; // Default to Colorado if not set
          
          // Convert appointment date and time to ISO format
          const appointmentDate = state.appointmentDate;
          const appointmentTime = state.appointmentTime;
          
          if (!appointmentDate || !appointmentTime) {
            showError('Booking Error', 'No appointment information available. Please try again.');
            return;
          }
          
          // Parse the appointment time and create ISO datetime
          const [timeStr, period] = appointmentTime.replace(/\s/g, '').split(/(am|pm)/i);
          const [hours, minutes] = timeStr.split(':');
          let hour24 = parseInt(hours);
          
          if (period.toLowerCase() === 'pm' && hour24 !== 12) {
            hour24 += 12;
          } else if (period.toLowerCase() === 'am' && hour24 === 12) {
            hour24 = 0;
          }
          
          // Create ISO datetime string manually without timezone processing
          // Parse the date string (assuming format like "2025-10-19")
          const [year, month, day] = appointmentDate.split('-');
          
          // Build the datetime string manually: "YYYY-MM-DDTHH:MM:SS.000Z"
          const isoDateTime = `${year}-${month}-${day}T${hour24.toString().padStart(2, '0')}:${(parseInt(minutes) || 0).toString().padStart(2, '0')}:00.000Z`;
          const providerName = getProviderNameFromAvailability(state.availabilities, 'Provider');
          
          const bookResult = await bookAppointment(
            providerCode,
            providerName,
            locationState,
            isoDateTime,
            createPatientResult.patientId,
            state.profileId,
            state.columnId,
            relationship,
            baseUrl,
            state.selectedAvailability?.locationCode ?? state.locationCode ?? '0000',
            state.selectedAvailability?.availabilityId
          );
          
          if (bookResult.success) {
            // Fire GTM event for successful appointment booking
            if ((window as any).dataLayer) {
              (window as any).dataLayer.push({
                event: 'I Success',
                intakeSource: 'IOI'
              });
            }
            
            navigate('/t');
          } else {
            const bookingErrorMsg = bookResult.error || 'Patient created but failed to book appointment. Please contact support at 425-640-7009.';
            pushAmdErrorToDataLayer(bookingErrorMsg);
            showError('Booking Failed', bookingErrorMsg);
            setIsSaving(false);
            return;
          }
        } catch (error) {
          const catchErrorMsg = error instanceof Error ? error.message : String(error);
          pushAmdErrorToDataLayer(catchErrorMsg);
          showError('Booking Error', 'Patient created but failed to book appointment. Please contact support at 425-640-7009.');
          setIsSaving(false);
          return;
        }
      } else {
        // Check for specific error code -2147217400 (duplicate patient)
        if (createPatientResult.errorCode === "-2147217400") {
          showDuplicatePatientError();
        } else {
          showError('Patient Creation Failed', createPatientResult.error || 'Failed to create patient. Please try again.');
        }
        setIsSaving(false);
        return;
      }
    } catch (error) {
      const systemErrorMsg = error instanceof Error ? error.message : 'An error occurred while creating the patient. Please try again.';
      pushAmdErrorToDataLayer(systemErrorMsg);
      showError('System Error', 'An error occurred while creating the patient. Please try again.');
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="screen-bg">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        
        {/* Provider Information - Outside the card */}
        <div style={{ 
          maxWidth: '420px',
          margin: '0 auto 24px auto',
          textAlign: 'left'
        }}>
          <ProviderImage
            className="mb-32"
            maxWidth={420}
            maxHeight={420}
          />
          <div className="ml-16"style={{ 
            color: '#185F93', 
            fontSize: '1rem', 
            fontWeight: '300', 
            marginBottom: '6px'
          }}>
            VISIT WITH
          </div>
          <h3 className="ml-16" style={{ 
            color: '#185F93', 
            fontSize: '1.9rem', 
            fontWeight: '700', 
            marginBottom: '18px',
            marginTop: '0'
          }}>
            {getProviderNameFromAvailability(state.availabilities, null)}
          </h3>
        </div>

        <main className="mtg-screen-main">
          <div style={{ maxWidth: 420, margin: '0 auto', marginBottom: 18 }}>
                        {/* Appointment Information Card */}
            <div className="appointment-card">
              <div className="appointment-section-title">When</div>
              <div className="appointment-detail" style={{ marginBottom: 2 }}>{sessionDate}</div>
              <div className="appointment-detail" style={{ marginBottom: 12 }}>{sessionTime}</div>
              <hr className="horizontal-line"/>
              <div className="appointment-section-title">Where</div>
              <div className="appointment-detail" style={{ whiteSpace: 'pre-line' }}>{sessionLocation}</div>
            </div>
            
            {/* Progress Bar */}
            <ProgressBar progress={90} />

            {/* Form Error Display */}
            {formError && (
              <div style={{
                background: '#DE5829',
                color: '#fff',
                padding: '12px 16px',
                marginBottom: '8px',
                marginTop: '12px'
              }}>
                <div style={{
                  fontWeight: '700',
                  fontSize: '1rem',
                  textDecoration: 'underline',
                  marginBottom: '4px'
                }}>
                  Error: Incomplete Form Submission
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  fontStyle: 'italic'
                }}>
                  Please ensure all required fields are filled out before submitting the form.
                </div>
              </div>
            )}
            
            {/* Main Form */}
            <form 
              className="form-container" 
              style={{ marginTop: 18 }}
              onSubmit={handleSubmit}
              aria-labelledby="form-title"
              aria-describedby="form-description"
              noValidate
            >

              

              {/* Reservation Timer - Fixed to top right */}
              <div style={{ 
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 1000,
                fontSize: '0.9rem', 
                fontWeight: 500,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: '8px 12px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e0e0e0'
              }}>
                <ReservationTimer />
              </div>
              
              {/* Form Title */}
              <h2 className="form-title" id="form-title">
                Please Complete<br />The Form Below
              </h2>

              <div id="form-description" className="sr-only">
                Patient intake form with personal information, insurance details, and communication preferences
              </div>
              
              {/* Personal Information Section */}
              <fieldset>
              <div className="form-field-group" style={{ marginBottom: 0 }}>
                <label htmlFor="first-name" className="form-field-label">
                  Name <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="first-name"
                  type="text"
                  placeholder="First"
                  value={first}
                  onChange={e => {
                    setFirst(e.target.value);
                    clearFieldError('first');
                  }}
                  className="form-field-input"
                  style={{ 
                    marginBottom: 12,
                    borderColor: fieldErrors.first ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.first ? '2px' : undefined,
                    boxShadow: fieldErrors.first ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                  aria-describedby="first-name-error"
                  aria-invalid={fieldErrors.first ? 'true' : 'false'}
                />
                <div id="first-name-error" className="sr-only" role="alert">
                  {first === '' ? 'First name is required' : ''}
                </div>
              </div>
              
              <div className="form-field-group-spacious">
                <input
                  id="last-name"
                  type="text"
                  placeholder="Last"
                  value={last}
                  onChange={e => {
                    setLast(e.target.value);
                    clearFieldError('last');
                  }}
                  className="form-field-input"
                  style={{
                    borderColor: fieldErrors.last ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.last ? '2px' : undefined,
                    boxShadow: fieldErrors.last ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                  aria-describedby="last-name-error"
                  aria-invalid={fieldErrors.last ? 'true' : 'false'}
                />
                <div id="last-name-error" className="sr-only" role="alert">
                  {last === '' ? 'Last name is required' : ''}
                </div>
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="date-of-birth" className="form-field-label">
                  Date Of Birth <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <div className="date-input-container">
                  <span className="date-input-icon" aria-hidden="true">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="3" stroke="#b5c7d6" strokeWidth="2"/><path d="M16 3v4M8 3v4" stroke="#b5c7d6" strokeWidth="2" strokeLinecap="round"/><path d="M3 9h18" stroke="#b5c7d6" strokeWidth="2"/></svg>
                  </span>
                  <input
                    id="date-of-birth"
                    type="date"
                    placeholder="MM/DD/YYYY"
                    value={dob}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={e => {
                      setDob(e.target.value);
                      clearFieldError('dob');
                    }}
                    className="date-input"
                    style={{ 
                      color: dob ? '#222' : '#b5c7d6',
                      borderColor: fieldErrors.dob ? '#dc3545' : undefined,
                      borderWidth: fieldErrors.dob ? '2px' : undefined,
                      boxShadow: fieldErrors.dob ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                    }}
                    required
                    aria-required="true"
                    aria-describedby="dob-error"
                    aria-invalid={fieldErrors.dob ? 'true' : 'false'}
                  />
                  <div id="dob-error" className="sr-only" role="alert">
                    {dob === '' ? 'Date of birth is required' : ''}
                  </div>
                </div>
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="sex" className="form-field-label">
                  Sex Assigned At Birth <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <select
                  id="sex"
                  value={sex}
                  onChange={e => {
                    setSex(e.target.value);
                    clearFieldError('sex');
                  }}
                  className={`form-field-select ${sex === '' ? 'unset-select' : ''}`}
                  style={{
                    borderColor: fieldErrors.sex ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.sex ? '2px' : undefined,
                    boxShadow: fieldErrors.sex ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                >
                  <option value="">Sex</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="phone" className="form-field-label">
                  Mobile Phone <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="555-555-5555"
                  value={phone}
                  onChange={e => {
                    setPhone(formatPhoneNumber(e.target.value));
                    clearFieldError('phone');
                  }}
                  className="form-field-input"
                  style={{ 
                    borderColor: fieldErrors.phone ? '#dc3545' : (phone && !isValidPhoneNumber(phone) ? '#f00' : undefined),
                    borderWidth: fieldErrors.phone ? '2px' : (phone && !isValidPhoneNumber(phone) ? '2px' : undefined),
                    boxShadow: fieldErrors.phone ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                  maxLength={12}
                />
                {phone && !isValidPhoneNumber(phone) && (
                  <div style={{ color: '#f00', fontSize: '0.9rem', marginTop: '4px' }}>
                    Please enter a valid phone number (555-555-5555)
                  </div>
                )}
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="email" className="form-field-label">
                  Email <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="email@email.com"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    clearFieldError('email');
                  }}
                  className="form-field-input"
                  style={{
                    borderColor: fieldErrors.email ? '#dc3545' : (email && !isValidEmail(email) ? '#f00' : undefined),
                    borderWidth: fieldErrors.email ? '2px' : undefined,
                    boxShadow: fieldErrors.email ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                />
                {email && !isValidEmail(email) && (
                  <div style={{ color: '#f00', fontSize: '0.9rem', marginTop: '4px' }}>
                    Please enter a valid email address
                  </div>
                )}
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="address" className="form-field-label">
                  Address <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="address"
                  type="text"
                  placeholder="55 Street Ave."
                  value={address}
                  onChange={e => {
                    setAddress(e.target.value);
                    clearFieldError('address');
                  }}
                  className="form-field-input"
                  style={{
                    borderColor: fieldErrors.address ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.address ? '2px' : undefined,
                    boxShadow: fieldErrors.address ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                />
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="city" className="form-field-label">
                  City <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="city"
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={e => {
                    setCity(e.target.value);
                    clearFieldError('city');
                  }}
                  className="form-field-input"
                  style={{
                    borderColor: fieldErrors.city ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.city ? '2px' : undefined,
                    boxShadow: fieldErrors.city ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                />
              </div>

              <div className="form-field-group-spacious">
                <label htmlFor="state" className="form-field-label">
                  State <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <select
                  id="state"
                  value={stateValue}
                  onChange={e => setStateValue(e.target.value)}
                  className="form-field-select"
                  required
                  aria-required="true"
                >
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
              </div>

              <div className="form-field-group-section">
                <label htmlFor="zip-code" className="form-field-label">
                  Zip Code <span className="required-indicator" aria-label="required field">*</span>
                </label>
                <input
                  id="zip-code"
                  type="text"
                  placeholder="55555"
                  value={zipCode}
                  onChange={e => {
                    setZipCode(formatZipCode(e.target.value));
                    clearFieldError('zipCode');
                  }}
                  className="form-field-input"
                  style={{ 
                    borderColor: fieldErrors.zipCode ? '#dc3545' : (zipCode && !isValidZipCode(zipCode) ? '#f00' : undefined),
                    borderWidth: fieldErrors.zipCode ? '2px' : (zipCode && !isValidZipCode(zipCode) ? '2px' : undefined),
                    boxShadow: fieldErrors.zipCode ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                  maxLength={10}
                />
                {zipCode && !isValidZipCode(zipCode) && (
                  <div style={{ color: '#f00', fontSize: '0.9rem', marginTop: '4px' }}>
                    Please enter a valid zip code (55555)
                  </div>
                )}
              </div>
              </fieldset>

              {/* Primary Subscriber Information Section - Hide if self-pay */}
              {hasInsurance(state.insurance) && (
              <fieldset>
                <div>
                  {/* Insurance Information Icon and Text - Only show if user has one or more insurances */}
                  {state.insurance && state.insurance.split(', ').length > 0 && (
                  <div 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      margin: '0 0 18px 0', 
                      padding: '0 12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowInsuranceModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowInsuranceModal(true);
                      }
                    }}
                    aria-label="Show insurance information"
                  >
                    <img 
                      src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMTAiIGZpbGw9IiMxOTc2ZDIiLz4KPGNpcmNsZSBjeD0iMTAiIGN5PSI2IiByPSIxLjUiIGZpbGw9IndoaXRlIi8+CjxyZWN0IHg9IjguNSIgeT0iOCIgd2lkdGg9IjMiIGhlaWdodD0iNiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg=="
                      alt="Information"
                      style={{
                        width: 20,
                        height: 20,
                        marginRight: 12,
                        flexShrink: 0
                      }}
                    />
                    <div style={{
                      fontSize: '0.75rem',
                      textDecoration: 'underline',
                      color: '#1976d2',
                      lineHeight: 1.4,
                      fontWeight: 400,
                      fontStyle: 'italic'
                    }}>
                      Please provide all of your insurance providers below.<br />You may choose multiple options.
                    </div>
                  </div>
                  )}

                  <div className="form-field-label" style={{ marginBottom: 12 }}>
                    I Am The<br />Primary Subscriber <span className="required-indicator" aria-label="required field">*</span>
                  </div>
                   <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="isPrimarySubscriber"
                        value="Yes"
                        checked={isPrimarySubscriber === 'Yes'}
                        onChange={e => setIsPrimarySubscriber(e.target.value)}
                        style={{
                          marginRight: 10,
                          width: 18,
                          height: 18,
                          minWidth: 18,
                          minHeight: 18,
                          maxWidth: 18,
                          maxHeight: 18,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          border: '1.5px solid #e69e6d',
                          borderRadius: '2px',
                          backgroundColor: isPrimarySubscriber === 'Yes' ? '#e69e6d' : 'transparent',
                          cursor: 'pointer',
                          position: 'relative',
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          padding: 0,
                          margin: '0 10px 0 0',
                          outline: 'none',
                          verticalAlign: 'top'
                        }}
                      />
                      <span style={{ fontSize: '1rem' }}>Yes</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="isPrimarySubscriber"
                        value="No"
                        checked={isPrimarySubscriber === 'No'}
                        onChange={e => setIsPrimarySubscriber(e.target.value)}
                        style={{
                          marginRight: 10,
                          width: 18,
                          height: 18,
                          minWidth: 18,
                          minHeight: 18,
                          maxWidth: 18,
                          maxHeight: 18,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          border: '1.5px solid #e69e6d',
                          borderRadius: '2px',
                          backgroundColor: isPrimarySubscriber === 'No' ? '#e69e6d' : 'transparent',
                          cursor: 'pointer',
                          position: 'relative',
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          padding: 0,
                          margin: '0 10px 0 0',
                          outline: 'none',
                          verticalAlign: 'top'
                        }}
                      />
                      <span style={{ fontSize: '1rem' }}>No</span>
                    </label>
                </div>
              </div>

              {isPrimarySubscriber === 'No' && (
                <>
              <label htmlFor="connection" className="form-field-label">
                Connection To Primary Subscriber <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <select
                id="connection"
                value={connectionToSubscriber}
                onChange={e => setConnectionToSubscriber(e.target.value)}
                className="form-field-select"
                style={{ marginBottom: 18 }}
                required
                aria-required="true"
              >
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Other">Other</option>
              </select>

              <div className="form-field-label">
                Primary Subscriber Name <span className="required-indicator" aria-label="required field">*</span>
              </div>
                </>
              )}
              
              {isPrimarySubscriber === 'No' && (
                <>
              <label htmlFor="subscriber-first" className="form-field-label">
                First Name <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <input
                id="subscriber-first"
                type="text"
                placeholder="First"
                value={subscriberFirst}
                onChange={e => {
                  setSubscriberFirst(e.target.value);
                  clearFieldError('subscriberFirst');
                }}
                className="form-field-input"
                style={{ 
                  marginBottom: 12,
                  borderColor: fieldErrors.subscriberFirst ? '#dc3545' : undefined,
                  borderWidth: fieldErrors.subscriberFirst ? '2px' : undefined,
                  boxShadow: fieldErrors.subscriberFirst ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                }}
                required
                aria-required="true"
              />
              
              <label htmlFor="subscriber-last" className="form-field-label">
                Last Name <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <input
                id="subscriber-last"
                type="text"
                placeholder="Last"
                value={subscriberLast}
                onChange={e => {
                  setSubscriberLast(e.target.value);
                  clearFieldError('subscriberLast');
                }}
                className="form-field-input"
                style={{ 
                  marginBottom: 18,
                  borderColor: fieldErrors.subscriberLast ? '#dc3545' : undefined,
                  borderWidth: fieldErrors.subscriberLast ? '2px' : undefined,
                  boxShadow: fieldErrors.subscriberLast ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                }}
                required
                aria-required="true"
              />

              <label htmlFor="subscriber-dob" className="form-field-label">
                Primary Subscriber Date Of Birth <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <div className="date-input-container">
                <span className="date-input-icon" aria-hidden="true">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="3" stroke="#b5c7d6" strokeWidth="2"/><path d="M16 3v4M8 3v4" stroke="#b5c7d6" strokeWidth="2" strokeLinecap="round"/><path d="M3 9h18" stroke="#b5c7d6" strokeWidth="2"/></svg>
                </span>
                <input
                  id="subscriber-dob"
                  type="date"
                  placeholder="MM/DD/YYYY"
                  value={subscriberDob}
                  onChange={e => {
                    setSubscriberDob(e.target.value);
                    clearFieldError('subscriberDob');
                  }}
                  className="date-input"
                  style={{ 
                    color: subscriberDob ? '#222' : '#b5c7d6',
                    borderColor: fieldErrors.subscriberDob ? '#dc3545' : undefined,
                    borderWidth: fieldErrors.subscriberDob ? '2px' : undefined,
                    boxShadow: fieldErrors.subscriberDob ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                  }}
                  required
                  aria-required="true"
                />
              </div>
                </>
              )}

              {isPrimarySubscriber === 'No' && (
                <>
              <div style={{ marginBottom: 12 }}>
                <div className="darkest-blue" style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 8 }}>
                  Primary Subscriber Has A Different Address <span className="required-indicator" aria-label="required field">*</span>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="subscriberDifferentAddress"
                      value="Yes"
                      checked={subscriberDifferentAddress === 'Yes'}
                      onChange={e => handleSubscriberDifferentAddressChange(e.target.value)}
                      style={{
                        marginRight: 10,
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        minHeight: 18,
                        maxWidth: 18,
                        maxHeight: 18,
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        border: '1.5px solid #e69e6d',
                        borderRadius: '2px',
                        backgroundColor: subscriberDifferentAddress === 'Yes' ? '#e69e6d' : 'transparent',
                        cursor: 'pointer',
                        position: 'relative',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        padding: 0,
                        margin: '0 10px 0 0',
                        outline: 'none',
                        verticalAlign: 'top'
                      }}
                    />
                    <span style={{ fontSize: '1rem' }}>Yes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="subscriberDifferentAddress"
                      value="No"
                      checked={subscriberDifferentAddress === 'No'}
                      onChange={e => handleSubscriberDifferentAddressChange(e.target.value)}
                      style={{
                        marginRight: 10,
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        minHeight: 18,
                        maxWidth: 18,
                        maxHeight: 18,
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        border: '1.5px solid #e69e6d',
                        borderRadius: '2px',
                        backgroundColor: subscriberDifferentAddress === 'No' ? '#e69e6d' : 'transparent',
                        cursor: 'pointer',
                        position: 'relative',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        padding: 0,
                        margin: '0 10px 0 0',
                        outline: 'none',
                        verticalAlign: 'top'
                      }}
                    />
                    <span style={{ fontSize: '1rem' }}>No</span>
                  </label>
                </div>
              </div>

              {subscriberDifferentAddress === 'Yes' && (
                <>
                  <div className="darkest-blue" style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 6 }}>
                    Primary Subscriber Address <span className="required-indicator" aria-label="required field">*</span>
                  </div>
                  
                  <input
                    type="text"
                    placeholder="55 Street Ave."
                    value={subscriberAddress}
                    onChange={e => setSubscriberAddress(e.target.value)}
                    className="form-field-input"
                    style={{ marginBottom: 18 }}
                    required
                    aria-required="true"
                  />

                  <input
                    type="text"
                    placeholder="City"
                    value={subscriberCity}
                    onChange={e => setSubscriberCity(e.target.value)}
                    className="form-field-input"
                    style={{ marginBottom: 18 }}
                    required
                    aria-required="true"
                  />

                  <select
                    value={subscriberState}
                    onChange={e => setSubscriberState(e.target.value)}
                    className="form-field-select"
                    style={{ marginBottom: 18 }}
                    required
                    aria-required="true"
                  >
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>

                  <input
                    type="text"
                        placeholder="55555"
                    value={subscriberZipCode}
                        onChange={e => {
                          setSubscriberZipCode(formatZipCode(e.target.value));
                          clearFieldError('subscriberZipCode');
                        }}
                        className="form-field-input"
                        style={{ 
                          marginBottom: 24,
                          borderColor: fieldErrors.subscriberZipCode ? '#dc3545' : (subscriberZipCode && !isValidZipCode(subscriberZipCode) ? '#f00' : undefined),
                          borderWidth: fieldErrors.subscriberZipCode ? '2px' : (subscriberZipCode && !isValidZipCode(subscriberZipCode) ? '2px' : undefined),
                          boxShadow: fieldErrors.subscriberZipCode ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                        }}
                    required
                    aria-required="true"
                        maxLength={10}
                      />
                      {subscriberZipCode && !isValidZipCode(subscriberZipCode) && (
                        <div style={{ color: '#f00', fontSize: '0.9rem', marginTop: '4px', marginBottom: '24px' }}>
                          Please enter a valid zip code (55555)
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              </fieldset>
              )}

              {/* Insurance Information Section - Hide if self-pay */}
              {hasInsurance(state.insurance) && (
              <div>
              <label htmlFor="insurance-provider" className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '1rem' }}>
                Primary Insurance Provider <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <select
                id="insurance-provider"
                value={insuranceProvider}
                onChange={e => {
                  setInsuranceProvider(e.target.value);
                  clearFieldError('insuranceProvider');
                }}
                className={`form-field-select ${insuranceProvider === '' ? 'unset-select' : ''}`}
                style={{ 
                  marginBottom: 18,
                  borderColor: fieldErrors.insuranceProvider ? '#dc3545' : undefined,
                  borderWidth: fieldErrors.insuranceProvider ? '2px' : undefined,
                  boxShadow: fieldErrors.insuranceProvider ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                }}
                required
                aria-required="true"
              >
                <option value="">Provider</option>
                {state.insurance && state.insurance.split(', ').map((insurance, index) => (
                  <option key={index} value={insurance.toLowerCase().replace(/\s+/g, '-')}>
                    {insurance}
                  </option>
                ))}
              </select>

              <label htmlFor="insurance-id" className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '1rem' }}>
                Primary Insurance ID <span className="required-indicator" aria-label="required field">*</span>
              </label>
              <input
                id="insurance-id"
                type="text"
                placeholder="555555555"
                value={insuranceId}
                onChange={e => {
                  setInsuranceId(e.target.value);
                  clearFieldError('insuranceId');
                }}
                style={{ 
                  width: '100%', 
                  marginBottom: 18, 
                  padding: '12px', 
                  borderRadius: 8, 
                  border: fieldErrors.insuranceId ? '2px solid #dc3545' : '1.5px solid #C2D4E4', 
                  fontSize: '1.08rem', 
                  fontWeight: 500,
                  boxShadow: fieldErrors.insuranceId ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                }}
                required
                aria-required="true"
              />

              {/* Dynamic Insurance Sections */}
              {state.insurance && state.insurance.split(', ').map((insurance, index) => {
                const insuranceLabels = ['Primary', 'Secondary', 'Tertiary'];
                const label = insuranceLabels[index] || `Insurance ${index + 1}`;
                const insuranceId = insurance.toLowerCase().replace(/\s+/g, '-');
                
                return (
                  <div key={index} style={{ marginBottom: 24 }} data-insurance-id={insuranceId}>

                    {/* Insurance Provider Selector (for Secondary and Tertiary) */}
                    {index > 0 && (
                      <>
                        <label htmlFor={`${insuranceId}-provider`} className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '1rem' }}>
                          {label} Insurance Provider <span className="required-indicator" aria-label="required field">*</span>
                        </label>
                        <select
                          id={`${insuranceId}-provider`}
                          value={index === 1 ? secondaryInsuranceProvider : tertiaryInsuranceProvider}
                          onChange={e => index === 1 ? setSecondaryInsuranceProvider(e.target.value) : setTertiaryInsuranceProvider(e.target.value)}
                          className={`form-field-select ${(index === 1 ? secondaryInsuranceProvider : tertiaryInsuranceProvider) === '' ? 'unset-select' : ''}`}
                          style={{ marginBottom: 18 }}
                          required
                          aria-required="true"
                        >
                          <option value="">Provider</option>
                          {(() => {
                            const currentValue = index === 1 ? secondaryInsuranceProvider : tertiaryInsuranceProvider;
                            const otherValue = index === 1 ? tertiaryInsuranceProvider : secondaryInsuranceProvider;
                            const available = getAvailableInsurances(currentValue, [otherValue]);
                            return available.map((ins, idx) => (
                              <option key={idx} value={ins.toLowerCase().replace(/\s+/g, '-')}>
                                {ins}
                              </option>
                            ));
                          })()}
                        </select>

                        <label htmlFor={`${insuranceId}-id`} className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '1rem' }}>
                          {label} Insurance ID <span className="required-indicator" aria-label="required field">*</span>
                        </label>
                        <input
                          id={`${insuranceId}-id`}
                          type="text"
                          placeholder="555555555"
                          value={index === 1 ? secondaryInsuranceId : tertiaryInsuranceId}
                          onChange={e => index === 1 ? setSecondaryInsuranceId(e.target.value) : setTertiaryInsuranceId(e.target.value)}
                          style={{ width: '100%', marginBottom: 18, padding: '12px', borderRadius: 8, border: '1.5px solid #C2D4E4', fontSize: '1.08rem', fontWeight: 500 }}
                          required
                          aria-required="true"
                        />
                      </>
                    )}
                    
                    {/* Front Card Upload */}
                    <div className="darkest-blue" style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 6 }}>
                      {label} Insurance Card (Front) <span className="required-indicator" aria-label="required field">*</span>
                    </div>
                    <div style={{ 
                      border: fieldErrors[`${insuranceId}-front`] ? '2px dashed #dc3545' : '2px dashed #C2D4E4', 
                      borderRadius: 8, 
                      padding: '20px', 
                      textAlign: 'center', 
                      marginBottom: 12,
                      backgroundColor: fieldErrors[`${insuranceId}-front`] ? '#fff5f5' : '#f8f9fa',
                      boxShadow: fieldErrors[`${insuranceId}-front`] ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                    }}>
                      {uploadedImages[`${insuranceId}-front`] ? (
                        // Show uploaded image preview
                        <div>
                          <img 
                            src={uploadedImages[`${insuranceId}-front`]} 
                            alt="Insurance card front"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <div className="darkest-blue" style={{ fontWeight: 600, marginBottom: 8 }}>
                            ✅ Front card uploaded successfully
                          </div>
                          <div style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: 12 }}>
                            Tap to upload a different photo
                          </div>
                        </div>
                      ) : (
                        // Show upload prompt
                        <div>
                          <div style={{ fontSize: '2rem', color: '#b5c7d6', marginBottom: 8 }}>
                            {isMobile() ? '📷' : '☁️'}
                          </div>
                          <div className="darkest-blue" style={{ fontWeight: 600, marginBottom: 4 }}>
                            {isMobile() ? 'Take a photo or choose a file' : 'Tap to upload photo'}
                          </div>
                          <div style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: 12 }}>
                            PNG, JPG, BMP, HEIC, WEBP, or PDF
                          </div>
                        </div>
                      )}
                      {/* iOS already offers Take Photo in its native picker — only add a dedicated camera button on other mobile devices */}
                      {isMobile() && !isIOS() ? (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'front');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-front-card-camera`}
                          />
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'front');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-front-card-upload`}
                          />
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <label htmlFor={`${insuranceId}-front-card-camera`} style={{
                              display: 'inline-block',
                              background: '#185F93',
                              color: '#fff',
                              padding: '8px 16px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600
                            }}>
                              Take Photo
                            </label>
                            <label htmlFor={`${insuranceId}-front-card-upload`} style={{
                              display: 'inline-block',
                              background: '#fff',
                              color: '#185F93',
                              padding: '8px 16px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              border: '1.5px solid #185F93'
                            }}>
                              Choose File
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'front');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-front-card-upload`}
                          />
                          <label htmlFor={`${insuranceId}-front-card-upload`} style={{
                            display: 'inline-block',
                            background: '#185F93',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 600
                          }}>
                            Choose File
                          </label>
                        </>
                      )}
                    </div>

                    {/* Back Card Upload */}
                    <div className="darkest-blue" style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 6 }}>
                      {label} Insurance Card (Back) <span className="required-indicator" aria-label="required field">*</span>
                    </div>
                    <div style={{ 
                      border: fieldErrors[`${insuranceId}-back`] ? '2px dashed #dc3545' : '2px dashed #C2D4E4', 
                      borderRadius: 8, 
                      padding: '20px', 
                      textAlign: 'center', 
                      marginBottom: 12,
                      backgroundColor: fieldErrors[`${insuranceId}-back`] ? '#fff5f5' : '#f8f9fa',
                      boxShadow: fieldErrors[`${insuranceId}-back`] ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
                    }}>
                      {uploadedImages[`${insuranceId}-back`] ? (
                        // Show uploaded image preview
                        <div>
                          <img 
                            src={uploadedImages[`${insuranceId}-back`]} 
                            alt="Insurance card back"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <div className="darkest-blue" style={{ fontWeight: 600, marginBottom: 8 }}>
                            ✅ Back card uploaded successfully
                          </div>
                          <div style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: 12 }}>
                            Tap to upload a different photo
                          </div>
                        </div>
                      ) : (
                        // Show upload prompt
                        <div>
                          <div style={{ fontSize: '2rem', color: '#b5c7d6', marginBottom: 8 }}>
                            {isMobile() ? '📷' : '☁️'}
                          </div>
                          <div className="darkest-blue" style={{ fontWeight: 600, marginBottom: 4 }}>
                            {isMobile() ? 'Take a photo or choose a file' : 'Tap to upload photo'}
                          </div>
                          <div style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: 12 }}>
                            PNG, JPG, BMP, HEIC, WEBP, or PDF
                          </div>
                        </div>
                      )}
                      {/* iOS already offers Take Photo in its native picker — only add a dedicated camera button on other mobile devices */}
                      {isMobile() && !isIOS() ? (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'back');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-back-card-camera`}
                          />
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'back');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-back-card-upload`}
                          />
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <label htmlFor={`${insuranceId}-back-card-camera`} style={{
                              display: 'inline-block',
                              background: '#185F93',
                              color: '#fff',
                              padding: '8px 16px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600
                            }}>
                              Take Photo
                            </label>
                            <label htmlFor={`${insuranceId}-back-card-upload`} style={{
                              display: 'inline-block',
                              background: '#fff',
                              color: '#185F93',
                              padding: '8px 16px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              border: '1.5px solid #185F93'
                            }}>
                              Choose File
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file, insuranceId, 'back');
                                e.target.value = '';
                              }
                            }}
                            style={{ display: 'none' }}
                            id={`${insuranceId}-back-card-upload`}
                          />
                          <label htmlFor={`${insuranceId}-back-card-upload`} style={{
                            display: 'inline-block',
                            background: '#185F93',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 600
                          }}>
                            Choose File
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
              )}

              {/* Communication Preferences Section */}
              <fieldset>
              <label htmlFor="heard-about-us" className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '1rem' }}>
                How Did You Hear About Us?
              </label>
              <select
                id="heard-about-us"
                value={heardAboutUs}
                onChange={e => {
                  setHeardAboutUs(e.target.value);
                  if (e.target.value !== MARKETING_SOURCE_OTHER) {
                    dispatch({ type: 'SET_SELF_REPORTED_SOURCE', payload: null });
                  }
                }}
                style={{ width: '100%', marginBottom: 18, padding: '12px', borderRadius: 8, border: '1.5px solid #C2D4E4', fontSize: '1.08rem', fontWeight: 500, backgroundColor: '#fff' }}
              >
                <option value="">Please select...</option>
                {MARKETING_SOURCE_OPTIONS.map(label => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
              {heardAboutUs === MARKETING_SOURCE_OTHER && (
                <div style={{ marginBottom: 18 }}>
                  <label htmlFor="marketing-source-other" className="darkest-blue" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.95rem' }}>
                    Please specify:
                  </label>
                  <input
                    id="marketing-source-other"
                    type="text"
                    value={state.selfReportedSource ?? ''}
                    onChange={e => dispatch({ type: 'SET_SELF_REPORTED_SOURCE', payload: e.target.value || null })}
                    placeholder="Enter how you heard about us"
                    style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1.5px solid #C2D4E4', fontSize: '1.08rem', fontWeight: 500, backgroundColor: '#fff' }}
                  />
                </div>
              )}

              {/* Only show SMS reminders checkbox if textConsent is not true */}
              {!(state.textConsent === true) && (
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer', 
                marginBottom: 24,
                lineHeight: 1
              }}>
                <input
                  type="checkbox"
                  checked={smsReminders}
                  onChange={e => setSmsReminders(e.target.checked)}
                  style={{
                    marginRight: 10,
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    minHeight: 18,
                    maxWidth: 18,
                    maxHeight: 18,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    border: '0.5px solid #e69e6d',
                    borderRadius: '2px',
                    backgroundColor: smsReminders ? '#e69e6d' : 'transparent',
                    cursor: 'pointer',
                    position: 'relative',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    padding: 0,
                    margin: '0 10px 0 0',
                    outline: 'none',
                    verticalAlign: 'top'
                  }}
                />
                <span style={{ 
                  fontSize: '1rem',
                  fontWeight: smsReminders ? 700 : 400,
                  color: smsReminders ? '#1a355e' : '#222',
                  fontStyle: smsReminders ? 'italic' : 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: "7px"
                }}>
                  Send me appointment reminders through SMS text.
                </span>
              </label>
              )}
              </fieldset>

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-button"
                aria-label="Submit intake form"
                disabled={isSaving}
              >
                <span>SUBMIT</span>
                <span style={{ fontSize: '1.2rem' }}>→</span>
              </button>
            </form>
          </div>
        </main>
      </div>
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
      />
      
      {/* Duplicate Patient Error Modal */}
      <DuplicatePatientErrorModal
        isOpen={duplicatePatientModal.isOpen}
        onClose={closeDuplicatePatientError}
        onRedirectToPortal={redirectToPortal}
      />
      
      {/* Insurance Modal */}
      {showInsuranceModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowInsuranceModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="insurance-modal-title"
          aria-describedby="insurance-modal-message"
        >
          <div
            style={{
              backgroundColor: '#185F93',
              borderRadius: '12px',
              padding: '24px',
              paddingTop: '48px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              color: '#fff'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowInsuranceModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#fff',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'right',
                width: '32px',
                height: '32px'
              }}
              aria-label="Close modal"
            >
              ×
            </button>

            {/* Modal content */}
            <div style={{ textAlign: 'center' }}>
              <p
                id="insurance-modal-message"
                style={{
                  fontSize: '0.9rem',
                  lineHeight: '1.4',
                  marginBottom: '20px',
                  marginTop: 0,
                  color: '#fff'
                }}
              >
                Please provide all of your insurance providers below (primary, secondary, and tertiary).<br />
                If your provider is out of network, you may be responsible for costs not covered by insurance.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Saving Modal */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #185F93',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }}
              />
            </div>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              color: '#185F93',
              fontSize: '1.2rem',
              fontWeight: '600'
            }}>
              Saving Your Information
            </h3>
            <p style={{ 
              margin: '0 0 20px 0', 
              color: '#666',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}>
              Please wait while we create your patient record and book your appointment. This may take a few moments.
            </p>
            <div style={{ 
              fontSize: '0.8rem', 
              color: '#999',
              fontStyle: 'italic'
            }}>
              Do not close this window or navigate away from this page.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenP; 